import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import HeroSlider from "../components/HeroSlider";
import AiRoomPreview from "../components/AiRoomPreview";
import photoStoryPosts from "../data/photoStoryPosts";
import { buildApiUrl, buildPublicFileUrl } from "../config/api";
import "./Home.css";
import "./HomeMobile.css";
import "./HomeMockupShowcase.css";
import "./HomeArchiveMarquee.css";

const toAssetUrl = (path) => {
  if (!path || typeof path !== "string") return "/images/logo192.png";
  if (/^(https?:)?\/\//i.test(path) || path.startsWith("data:")) return path;
  if (path.startsWith("/uploads") || path.startsWith("uploads/")) {
    return buildPublicFileUrl(path);
  }
  return path;
};

const getField = (item, names, fallback = "") => {
  for (const name of names) {
    if (item && item[name] !== undefined && item[name] !== null && item[name] !== "") {
      return item[name];
    }
  }
  return fallback;
};

const toBool = (value) => value === true || value === 1 || value === "1" || value === "true";

const productKey = (product) =>
  String(getField(product, ["id", "productId", "product_id", "slug", "title"], "product"));

const brandSlugOf = (product) =>
  String(getField(product, ["brandSlug", "brand_slug"], "")).toLowerCase();

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value !== "string" || !value.trim()) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
};

const normalizeProduct = (product) => {
  const brandSlug = brandSlugOf(product);
  const previewImages = parseJsonArray(
    getField(product, ["previewImages", "preview_images", "previewImagesJson", "preview_images_json"])
  );
  const mockups = parseJsonArray(getField(product, ["mockups", "mockupsJson", "mockups_json"]));
  const coverPath = getField(product, ["coverImage", "cover_image"]);
  const thumbnailPath = getField(product, ["thumbnailImage", "thumbnail_image", "thumbnail"]);

  const primaryPath = getField(product, [
    "thumbnailImage",
    "thumbnail_image",
    "thumbnail",
    "coverImage",
    "cover_image",
    "image",
    "previewImage",
    "preview_image",
  ]);

  return {
    ...product,
    id: getField(product, ["id", "productId", "product_id", "slug"]),
    productId: getField(product, ["productId", "product_id"]),
    productType: getField(product, ["productType", "product_type"]),
    brandSlug,
    brandName: getField(product, ["brandName", "brand_name", "brand"], "MayaChitro"),
    category: getField(product, ["category"], ""),
    categorySlug: getField(product, ["categorySlug", "category_slug"], ""),
    collection: getField(product, ["collection", "collectionName", "collection_name"], ""),
    altText: getField(product, ["altText", "alt_text", "title"], "MayaChitro product image"),
    shortTitle: getField(product, ["shortTitle", "short_title"]),
    image: toAssetUrl(primaryPath),
    thumbnailImage: thumbnailPath ? toAssetUrl(thumbnailPath) : toAssetUrl(primaryPath),
    coverImage: coverPath ? toAssetUrl(coverPath) : "",
    previewImages,
    mockups,
    previewImagesJson: previewImages,
    mockupsJson: mockups,
    personalPrice: getField(product, ["personalPrice", "personal_price", "price"], 0),
    commercialPrice: getField(product, ["commercialPrice", "commercial_price"], 0),
    editorialPrice: getField(product, ["editorialPrice", "editorial_price"], 0),
    isFeatured: getField(product, ["isFeatured", "is_featured", "featured"], 0),
    homepageLatest: getField(product, ["homepageLatest", "homepage_latest"], 0),
    homepageLatestOrder: getField(product, ["homepageLatestOrder", "homepage_latest_order"], null),
    isNew: getField(product, ["isNew", "is_new"], 0),
    isTrending: getField(product, ["isTrending", "is_trending"], 0),
    listingPriority: getField(product, ["listingPriority", "listing_priority"]),
    salesPriority: getField(product, ["salesPriority", "sales_priority"]),
    modelReleaseStatus: getField(product, ["modelReleaseStatus", "model_release_status"]),
    licensingNotes: getField(product, ["licensingNotes", "licensing_notes"]),
  };
};

const getPriorityScore = (product) => {
  const listingPriority = String(getField(product, ["listingPriority", "listing_priority"], "")).toLowerCase();
  const salesPriority = String(getField(product, ["salesPriority", "sales_priority"], "")).toLowerCase();
  const priorityMap = { high: 30, medium: 15, low: 5 };
  const productId = String(getField(product, ["productId", "product_id"], ""));
  const numericId = Number(productId.replace(/\D/g, "")) || 0;

  return (
    (toBool(getField(product, ["isFeatured", "is_featured"])) ? 100 : 0) +
    (toBool(getField(product, ["featured"])) ? 90 : 0) +
    (toBool(getField(product, ["isNew", "is_new"])) ? 35 : 0) +
    (toBool(getField(product, ["isTrending", "is_trending"])) ? 20 : 0) +
    (priorityMap[listingPriority] || 0) +
    (priorityMap[salesPriority] || 0) +
    numericId / 1000
  );
};

const sortProducts = (items) => [...items].sort((a, b) => getPriorityScore(b) - getPriorityScore(a));

const slugifyText = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const photoCollectionTemplates = [
  {
    label: "Photo Collection",
    title: "Golden River Sunsets",
    text: "Warm river light, silhouettes, and peaceful evening scenes.",
    categorySlugs: ["sunset-golden-hour", "landscape-nature"],
    keywords: ["sunset", "golden", "river", "bird"],
    preferredProductCodes: ["MC-0008", "MC-0012", "MC-0014", "MC-0015", "MC-0016"],
    link: "/shop?brand=mayachitro&category=landscape-nature",
  },
  {
    label: "Photo Collection",
    title: "River & Boat Life",
    text: "Quiet boats, water journeys, and rural river moments.",
    categorySlugs: ["river-boat-water", "landscape-nature"],
    keywords: ["river", "boat", "water", "shore"],
    preferredProductCodes: ["MC-0014", "MC-0015", "MC-0016"],
    link: "/shop?brand=mayachitro&category=river-boat-water",
  },
  {
    label: "Photo Collection",
    title: "Nature & Macro",
    text: "Leaves, insects, flowers, water drops, and natural details.",
    categorySlugs: ["macro-details", "flowers-botanical"],
    keywords: ["macro", "leaf", "spider", "dew", "drops"],
    preferredProductCodes: ["MC-0006", "MC-0007", "MC-0010"],
    link: "/shop?brand=mayachitro&category=macro-details",
  },
  {
    label: "Photo Collection",
    title: "Flowers & Botanical",
    text: "Soft floral details, color, texture, and wall art moods.",
    categorySlugs: ["flowers-botanical"],
    keywords: ["flower", "botanical", "bougainvillea", "bloom"],
    preferredProductCodes: ["MC-0005"],
    link: "/shop?brand=mayachitro&category=flowers-botanical",
  },
  {
    label: "Photo Collection",
    title: "Street Stories",
    text: "Human moments, daily life, documentary frames, and culture.",
    categorySlugs: ["street-documentary", "people-portrait", "travel-culture"],
    keywords: ["street", "people", "portrait", "daily", "culture"],
    preferredProductCodes: ["MC-0009"],
    link: "/shop?brand=mayachitro&category=street-documentary",
  },
  {
    label: "Photo Collection",
    title: "Clouds & Minimal Mood",
    text: "Open sky, cloudy tones, calm negative space, and atmosphere.",
    categorySlugs: ["abstract-minimal", "landscape-nature"],
    keywords: ["cloud", "sky", "minimal", "abstract"],
    preferredProductCodes: ["MC-0010"],
    link: "/shop?brand=mayachitro&category=abstract-minimal",
  },
];

const wallArtCollectionTemplates = [
  {
    label: "Wall Art Collection",
    title: "Animal Posters",
    text: "Character wall art, pets, wildlife, and playful printable decor.",
    categorySlugs: ["animal-posters"],
    keywords: ["animal", "cat", "black cat", "pet", "wildlife"],
    preferredProductCodes: ["CPS-0002"],
    link: "/shop?brand=cloudy-pixel-studio&category=animal-posters",
  },
  {
    label: "Wall Art Collection",
    title: "Rainy Street Posters",
    text: "Moody alleys, cozy cafés, night rain, and cinematic street scenes.",
    categorySlugs: ["rainy-street-posters", "coffee-cafe-posters", "cozy-village-posters"],
    keywords: ["rainy", "street", "alley", "japanese", "cafe", "village"],
    preferredProductCodes: ["CPS-0001"],
    link: "/shop?brand=cloudy-pixel-studio&category=rainy-street-posters",
  },
  {
    label: "Wall Art Collection",
    title: "Botanical & Floral",
    text: "Soft flowers, leaves, calm natural tones, and elegant wall art.",
    categorySlugs: ["botanical-floral-posters"],
    keywords: ["botanical", "floral", "flower", "leaf", "green"],
    preferredProductCodes: [],
    link: "/shop?brand=cloudy-pixel-studio&category=botanical-floral-posters",
  },
  {
    label: "Wall Art Collection",
    title: "Lake House & Coastal",
    text: "Peaceful water, coastal moods, lake homes, and airy interiors.",
    categorySlugs: ["coastal-lake-house-posters"],
    keywords: ["coastal", "lake", "water", "boat", "sea"],
    preferredProductCodes: [],
    link: "/shop?brand=cloudy-pixel-studio&category=coastal-lake-house-posters",
  },
  {
    label: "Wall Art Collection",
    title: "Abstract Minimal",
    text: "Clean compositions, neutral spaces, simple shapes, and calm decor.",
    categorySlugs: ["abstract-minimal-posters"],
    keywords: ["abstract", "minimal", "neutral", "modern"],
    preferredProductCodes: [],
    link: "/shop?brand=cloudy-pixel-studio&category=abstract-minimal-posters",
  },
  {
    label: "Wall Art Collection",
    title: "Vintage Travel",
    text: "Retro-inspired scenes, warm nostalgic color, and collectible poster style.",
    categorySlugs: ["vintage-travel-posters", "retro-vintage-posters"],
    keywords: ["vintage", "retro", "travel", "poster"],
    preferredProductCodes: [],
    link: "/shop?brand=cloudy-pixel-studio&category=vintage-travel-posters",
  },
];

const shopUses = [
  { title: "For your home", text: "Personal display and wall-art printing.", license: "Personal license", icon: "⌂", link: "/shop?q=wall+art" },
  { title: "For websites", text: "Blogs, portfolios and digital publishing.", license: "Personal or commercial", icon: "◫", link: "/shop?q=website" },
  { title: "For editorial", text: "Articles, education and documentary stories.", license: "Editorial license", icon: "¶", link: "/shop?q=editorial" },
  { title: "For campaigns", text: "Brand, advertising and commercial projects.", license: "Commercial license", icon: "◎", link: "/shop?q=commercial" },
];

const visualDiary = [
  ["/images/chicken-seller-on-bicycle-old-man-urban-street-life-portrait-dhaka-mayachitro.jpg", "Street life", "Dhaka"],
  ["/images/girl-with-sunflower-bouquet-smiling-portrait-street-market-mayachitro.jpg", "Market portrait", "Bangladesh"],
  ["/images/dhaka-river-commute-wooden-boats-people-transport-lifestyle-mayachitro.jpg", "River commute", "Dhaka"],
  ["/images/deep-pink-frangipani-flower-closeup-garden-bokeh-tropical-bloom-mayachitro.jpg", "Garden study", "Natural details"],
  ["/images/golden-hour-canoe-river-reflection-dramatic-clouds-mayachitro.jpg", "Golden hour", "River journal"],
  ["/images/colorful-balloons-night-dhaka-street-photography.jpg", "Night colour", "Dhaka"],
  ["/images/fisherman-and-wife-on-wooden-boat-bangladesh-river-life-mayachitro.jpg", "River life", "Bangladesh"],
  ["/images/floating-frangipani-flowers-water-bowl-aesthetic-decor-mayachitro.jpg", "Quiet arrangement", "Flower study"],
  ["/images/dhaka-city-night-traffic-lighttrails-rainy-road-longexposure-mayachitro.jpg", "City motion", "Dhaka at night"],
  ["/images/bangladeshi-man-colorful-panjabi-street-portrait-traditional-cap-cultural-festival-mayachitro.jpg", "Festival portrait", "Cultural journal"],
];

const getCardImage = (product) =>
  toAssetUrl(
    getField(product, [
      "coverImage",
      "cover_image",
      "thumbnailImage",
      "thumbnail_image",
      "thumbnail",
      "image",
      "previewImage",
      "preview_image",
    ])
  );

const getMockupImages = (product) => {
  const mockups = parseJsonArray(getField(product, ["mockups", "mockupsJson", "mockups_json"]));
  const images = mockups.map((item) => {
    const path = typeof item === "string" ? item : item?.path || item?.image || item?.src;
    return path ? toAssetUrl(path) : "";
  }).filter(Boolean);
  const card = getCardImage(product);
  return [...new Set([...images, card].filter(Boolean))].slice(0, 2);
};

const getProductSerialNumber = (product) => {
  const code = String(
    getField(product, ["productCode", "product_code", "productId", "product_id", "code", "sku"], "")
  );

  const match = code.match(/\d+/);

  if (match) {
    return Number(match[0]);
  }

  return Number(getField(product, ["id"], 0)) || 0;
};

const canonicalPhotoCategory = (value) => {
  const text = slugifyText(value);
  if (!text) return null;
  if (text.includes("nature") && text.includes("landscape")) return { key: "nature-landscapes", title: "Nature & Landscapes" };
  if (text.includes("people") && text.includes("portrait")) return { key: "people-portrait", title: "People & Portrait" };
  if (text.includes("people") && text.includes("culture")) return { key: "people-culture", title: "People & Culture" };
  if (text.includes("river") || text.includes("boat") || text.includes("water")) return { key: "river-water", title: "River, Boat & Water" };
  if (text.includes("wildlife") && text.includes("birds")) return { key: "wildlife-birds", title: "Wildlife & Birds" };
  if (text.includes("wildlife") && text.includes("animal")) return { key: "wildlife-animals", title: "Wildlife & Animals" };
  if (text.includes("wildlife") && text.includes("nature")) return { key: "wildlife-nature", title: "Wildlife & Nature" };
  if (text.includes("flower") || text.includes("botanical")) return { key: "flowers-botanical", title: "Flowers & Botanical" };
  if (text.includes("macro")) return { key: "nature-macro", title: "Nature & Macro" };
  if (text.includes("abstract")) return { key: "abstract-minimal", title: "Abstract & Minimal" };
  if (text.includes("architecture")) return { key: "architecture-places", title: "Architecture & Places" };
  if (text.includes("city") || text.includes("street")) return { key: "city-street", title: "City & Street Photography" };
  if (text.includes("travel")) return { key: "travel-places", title: "Travel & Places" };
  return { key: text, title: String(value).replace(/\s+/g, " ").trim() };
};

const featuredCollectionOrder = [
  "nature-landscapes",
  "people-culture",
  "nature-macro",
  "people-portrait",
  "wildlife-birds",
  "river-water",
  "architecture-places",
  "flowers-botanical",
  "wildlife-animals",
  "city-street",
];

const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [roomPreviewProduct, setRoomPreviewProduct] = useState(null);
  const [mockupSlide, setMockupSlide] = useState(0);
  const [mockupPaused, setMockupPaused] = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(buildApiUrl("/products?status=published"), {
        headers: { Accept: "application/json" },
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to load products.");
      }

      const normalizedProducts = Array.isArray(data.products)
        ? data.products.map(normalizeProduct)
        : [];

      setProducts(normalizedProducts);
    } catch (err) {
      setError(err.message || "Could not load products from database.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const mayachitroAll = useMemo(
    () => sortProducts(products.filter((item) => brandSlugOf(item) === "mayachitro")),
    [products]
  );

  const cloudyAll = useMemo(
    () => sortProducts(products.filter((item) => brandSlugOf(item) === "cloudy-pixel-studio")),
    [products]
  );

  const featuredMayaProducts = useMemo(() => {
    const curated = mayachitroAll
      .filter((product) => toBool(getField(product, ["homepageLatest", "homepage_latest"], false)))
      .sort((a, b) => {
        const aOrder = Number(getField(a, ["homepageLatestOrder", "homepage_latest_order"], 9999)) || 9999;
        const bOrder = Number(getField(b, ["homepageLatestOrder", "homepage_latest_order"], 9999)) || 9999;
        return aOrder - bOrder || getProductSerialNumber(b) - getProductSerialNumber(a);
      });

    if (curated.length) return curated.slice(0, 10);

    return [...mayachitroAll]
      .sort((a, b) => getProductSerialNumber(b) - getProductSerialNumber(a))
      .slice(0, 10);
  }, [mayachitroAll]);

  const archiveImages = useMemo(() => mayachitroAll.map((product) => ({
    src: getCardImage(product),
    title: product.title || product.productId,
    slug: product.slug,
  })).filter((item) => item.src), [mayachitroAll]);

  const latestStories = useMemo(
    () => photoStoryPosts.filter((story) => story.brand === "MayaChitro").slice(0, 3),
    []
  );

  const defaultRoomPreviewProduct = featuredMayaProducts[0];

  const mockupProducts = useMemo(
    () => mayachitroAll.map((product) => ({ product, images: getMockupImages(product) })).filter((item) => item.images.length >= 2),
    [mayachitroAll]
  );

  useEffect(() => {
    if (mockupPaused || mockupProducts.length < 2) return undefined;
    const timer = window.setInterval(() => setMockupSlide((current) => (current + 1) % mockupProducts.length), 5200);
    return () => window.clearInterval(timer);
  }, [mockupPaused, mockupProducts.length]);

  useEffect(() => {
    if (mockupSlide >= mockupProducts.length && mockupProducts.length) setMockupSlide(0);
  }, [mockupSlide, mockupProducts.length]);

  const openRoomPreview = () => {
    if (defaultRoomPreviewProduct) setRoomPreviewProduct(defaultRoomPreviewProduct);
  };

  const photoCollections = useMemo(() => {
    const groups = new Map();
    mayachitroAll.forEach((product) => {
      const rawCategory = getField(product, ["category", "categorySlug", "category_slug"], "");
      const category = canonicalPhotoCategory(rawCategory);
      if (!category) return;
      if (!groups.has(category.key)) groups.set(category.key, { ...category, sourceProduct: product, count: 0, sourceCategory: rawCategory });
      const group = groups.get(category.key);
      group.count += 1;
      if (getProductSerialNumber(product) > getProductSerialNumber(group.sourceProduct)) group.sourceProduct = product;
    });
    return [...groups.values()].filter((group) => featuredCollectionOrder.includes(group.key)).sort((a, b) => featuredCollectionOrder.indexOf(a.key) - featuredCollectionOrder.indexOf(b.key)).map((group) => ({
      label: `${group.count} photograph${group.count === 1 ? "" : "s"}`,
      title: group.title,
      text: `Explore ${group.count} original MayaChitro photograph${group.count === 1 ? "" : "s"} in this collection.`,
      link: `/shop?brand=mayachitro&category=${encodeURIComponent(slugifyText(group.sourceCategory))}`,
      image: getCardImage(group.sourceProduct),
    })).filter((collection) => collection.image);
  }, [mayachitroAll]);

  const wallArtCollections = useMemo(() => {
    return wallArtCollectionTemplates
      .map((collection, index) => {
        const preferredProduct = cloudyAll.find((product) =>
          collection.preferredProductCodes.includes(
            String(getField(product, ["productCode", "product_code", "productId", "product_id"], ""))
          )
        );

        const categoryProduct = cloudyAll.find((product) => {
          const productCategorySlug = slugifyText(
            getField(product, ["categorySlug", "category_slug", "category"], "")
          );

          return collection.categorySlugs.some(
            (slug) => productCategorySlug === slug || productCategorySlug.includes(slug)
          );
        });

        const keywordProduct = cloudyAll.find((product) => {
          const searchableText = slugifyText(
            [
              getField(product, ["title"], ""),
              getField(product, ["shortTitle", "short_title"], ""),
              getField(product, ["categorySlug", "category_slug", "category"], ""),
              getField(product, ["collection", "collectionName", "collection_name"], ""),
            ].join(" ")
          );

          return collection.keywords.some((keyword) =>
            searchableText.includes(slugifyText(keyword))
          );
        });

        const sourceProduct =
          preferredProduct ||
          categoryProduct ||
          keywordProduct ||
          cloudyAll[index % Math.max(cloudyAll.length, 1)];

        return {
          ...collection,
          image: sourceProduct ? getCardImage(sourceProduct) : "",
        };
      })
      .filter((collection) => collection.image)
      .slice(0, 6);
  }, [cloudyAll]);

  const featuredCloudyProducts = useMemo(() => {
  return cloudyAll.slice(0, 12);
}, [cloudyAll]);

  const latestWallArtProducts = useMemo(() => {
    return [...cloudyAll]
      .sort((a, b) => getProductSerialNumber(b) - getProductSerialNumber(a))
      .slice(0, 6);
  }, [cloudyAll]);

  return (
    <main className="home-page">
      <HeroSlider />

      <section className="market-section featured-section">
        <div className="section-heading">
          <div>
            <h2>Latest Photo Downloads</h2>
          </div>
          <Link to="/shop?brand=mayachitro">View all photos →</Link>
        </div>

        {loading && <div className="home-product-skeletons" aria-label="Loading photographs">{Array.from({ length: 4 }).map((_, index) => <span key={index} />)}</div>}
        {error && <div className="home-error-text"><span>{error}</span><button type="button" onClick={fetchProducts}>Try again</button></div>}

        <div className="product-grid featured-product-grid">
          {featuredMayaProducts.map((product) => (
            <ProductCard product={product} key={productKey(product)} />
          ))}
        </div>
      </section>

      {archiveImages.length > 0 && <section className="home-archive-marquee" aria-labelledby="archive-marquee-title">
        <div className="home-archive-copy"><p className="section-kicker">The complete photography archive</p><h2 id="archive-marquee-title">Looking for something particular?</h2><p>Explore original photographs from street life, landscapes, macro details, wildlife, river moments, and quiet everyday scenes.</p><Link to="/shop?brand=mayachitro">Browse all photographs <span aria-hidden="true">→</span></Link></div>
        <div className="home-archive-ribbons" onMouseEnter={(event) => event.currentTarget.classList.add("paused")} onMouseLeave={(event) => event.currentTarget.classList.remove("paused")}>
          <div className="home-archive-ribbon home-archive-ribbon-forward">{[...archiveImages, ...archiveImages].map((item, index) => <Link to={item.slug ? `/product/${item.slug}` : "/shop"} key={`archive-a-${index}-${item.slug}`} title={item.title}><img src={item.src} alt="" loading="lazy" /></Link>)}</div>
          <div className="home-archive-ribbon home-archive-ribbon-reverse">{[...archiveImages.slice().reverse(), ...archiveImages.slice().reverse()].map((item, index) => <Link to={item.slug ? `/product/${item.slug}` : "/shop"} key={`archive-b-${index}-${item.slug}`} title={item.title}><img src={item.src} alt="" loading="lazy" /></Link>)}</div>
        </div>
      </section>}

      <section className="market-section collections-section mc-photo-collections-section maya-photo-collections-section">
        <div className="section-heading mc-photo-collections-heading">
          <div>
            <p className="section-kicker">Browse by Mood</p>
            <h2>Photography Collections</h2>
          </div>

          <Link to="/shop?brand=mayachitro">View all collections →</Link>
        </div>

        <div className="mc-photo-collection-rail home-editorial-collections">
          {photoCollections.map((collection) => (
            <Link className="mc-photo-collection-card" to={collection.link} key={collection.title}>
              <img src={collection.image} alt={collection.title} loading="lazy" />

              <div className="mc-photo-collection-shade" />

              <div className="mc-photo-collection-copy">
                <span>{collection.label}</span>
                <h3>{collection.title}</h3>
                <p>{collection.text}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {mockupProducts.length > 0 && (() => {
        const slide = mockupProducts[mockupSlide] || mockupProducts[0];
        const product = slide.product;
        return <section className="home-mockup-showcase" aria-labelledby="mockup-showcase-title" onMouseEnter={() => setMockupPaused(true)} onMouseLeave={() => setMockupPaused(false)} onFocus={() => setMockupPaused(true)} onBlur={() => setMockupPaused(false)}>
          <div className="home-mockup-copy"><p className="section-kicker">See it styled</p><h2 id="mockup-showcase-title">A photograph that belongs in your space.</h2><p>Explore the same MayaChitro image as a finished room scene and a closer presentation view. This is how your download can become wall art.</p><Link to={`/product/${product.slug}`}>View this photograph <span aria-hidden="true">→</span></Link><div className="home-mockup-progress"><strong>{String(mockupSlide + 1).padStart(2, "0")}</strong><span>/ {String(mockupProducts.length).padStart(2, "0")}</span><div className="home-mockup-dots" role="tablist" aria-label="Mockup examples">{mockupProducts.map((item, index) => <button key={productKey(item.product)} type="button" className={index === mockupSlide ? "active" : ""} onClick={() => setMockupSlide(index)} aria-label={`Show ${item.product.title}`} aria-selected={index === mockupSlide} />)}</div></div></div>
          <div className="home-mockup-stage"><div className="home-mockup-frame home-mockup-frame-primary"><img key={`${productKey(product)}-primary`} src={slide.images[0]} alt={`${product.title} shown as framed wall art`} /></div><div className="home-mockup-frame home-mockup-frame-secondary"><img key={`${productKey(product)}-secondary`} src={slide.images[1]} alt="" /></div><span className="home-mockup-label">Real product mockups</span></div>
        </section>;
      })()}

      <section className="home-room-preview" aria-labelledby="room-preview-title">
        <div className="home-room-preview-visual" aria-hidden="true">
          <div className="home-room-preview-wall">
            {defaultRoomPreviewProduct && <img className="home-room-preview-framed-art" src={getCardImage(defaultRoomPreviewProduct)} alt="" />}
            <span className="home-room-preview-sofa" /><span className="home-room-preview-lamp" />
          </div>
          <span className="home-room-preview-badge">Your room · Your photograph</span>
        </div>
        <div className="home-room-preview-copy">
          <p className="section-kicker">Free Room &amp; AR Studio</p>
          <h2 id="room-preview-title">See it in your space.</h2>
          <p>Upload a room photo or use your camera, then position the artwork before you choose. Your image stays on your device.</p>
          <div className="home-room-preview-actions">
            <button type="button" onClick={openRoomPreview} disabled={!defaultRoomPreviewProduct}>{defaultRoomPreviewProduct ? "Try room preview" : "Preparing preview…"}</button>
            <Link to="/shop?brand=mayachitro">Choose another photograph →</Link>
          </div>
          <ul className="home-room-preview-points" aria-label="Preview features"><li>Room photo</li><li>Live camera</li><li>Private by design</li></ul>
        </div>
      </section>

      <section className="story-vlog-grid home-story-editorial" aria-labelledby="home-story-title">
        <div className="home-story-collage" aria-label="Selected photo stories">
          {latestStories.slice(0, 3).map((story, index) => (
            <Link className={`home-story-image home-story-image-${index + 1}`} to={`/stories/${story.slug}`} key={story.slug}>
              <img src={toAssetUrl(story.thumbnail || story.image)} alt={story.imageAlt || story.title} loading="lazy" />
              <span>{story.title}</span>
            </Link>
          ))}
        </div>

        <div className="home-story-copy">
          <p className="section-kicker">Stories behind the photographs</p>
          <h2 id="home-story-title">More than an image. A record of light, place and feeling.</h2>
          <p>Each MayaChitro photograph begins with a real encounter—a quiet river, changing weather, street life or a detail that asks for a closer look.</p>
          <p>Explore the context, choices and field notes behind the work, then find photographs that carry meaning beyond the frame.</p>
          <div className="home-story-actions">
            <Link to="/stories">Read photo stories →</Link>
            <Link to="/vlog">Behind the frame →</Link>
          </div>
        </div>
      </section>

      <section className="market-section home-use-section" aria-labelledby="home-use-title">
        <div className="section-heading">
          <div><p className="section-kicker">Start with your purpose</p><h2 id="home-use-title">Choose the right image and license.</h2></div>
          <Link to="/licensing">Compare licenses →</Link>
        </div>
        <div className="home-use-grid">
          {shopUses.map((item) => (
            <Link to={item.link} className="home-use-card" key={item.title}>
              <span aria-hidden="true">{item.icon}</span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
              <small>{item.license}</small>
              <b>Explore photographs →</b>
            </Link>
          ))}
        </div>
        <div className="home-use-license-note">
          <p>Not sure which license applies? Review usage rights, release requirements and permitted projects before purchasing.</p>
          <Link to="/licensing">Understand licensing →</Link>
        </div>
      </section>

      {false && featuredCloudyProducts.length > 0 && (
  <section className="market-section featured-section cps-featured-wall-art-section">
    <div className="section-heading">
      <div>
        <p className="section-kicker">Premium Printable Wall Art</p>
        <h2>Featured Cloudy Pixel Studio Wall Arts</h2>
      </div>

      <Link to="/shop?brand=cloudy-pixel-studio">View all wall art →</Link>
    </div>

    <div className="product-grid featured-product-grid">
      {featuredCloudyProducts.map((product) => (
        <ProductCard product={product} key={productKey(product)} />
      ))}
    </div>
  </section>
)}

      {false && wallArtCollections.length > 0 && (
        <section className="market-section collections-section mc-photo-collections-section cps-wall-collections-section cps-collections-section">
          <div className="section-heading mc-photo-collections-heading">
            <div>
              <p className="section-kicker">Browse by Theme</p>
              <h2>Wall Art Collections</h2>
            </div>

            <Link to="/shop?brand=cloudy-pixel-studio">View all wall art collections →</Link>
          </div>

          <div className="mc-photo-collection-rail">
            {wallArtCollections.map((collection) => (
              <Link className="mc-photo-collection-card" to={collection.link} key={collection.title}>
                <img src={collection.image} alt={collection.title} loading="lazy" />

                <div className="mc-photo-collection-shade" />

                <div className="mc-photo-collection-copy">
                  <span>{collection.label}</span>
                  <h3>{collection.title}</h3>
                  <p>{collection.text}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {false && latestWallArtProducts.length > 0 && (
        <section className="market-section compact-section cps-latest-wall-art-section">
          <div className="section-heading">
            <div>
              <p className="section-kicker">New Arrivals</p>
              <h2>Latest Wall Art Downloads</h2>
            </div>

            <Link to="/shop?brand=cloudy-pixel-studio&sort=newest">
              Shop latest posters →
            </Link>
          </div>

          <div className="product-grid secondary-product-grid">
            {latestWallArtProducts.map((product) => (
              <ProductCard product={product} compact key={productKey(product)} />
            ))}
          </div>
        </section>
      )}

      <section className="home-diary" aria-labelledby="home-diary-title">
        <div className="section-heading">
          <div>
            <p className="section-kicker">Scenes between stories</p>
            <h2 id="home-diary-title">Visual Diary</h2>
          </div>
          <Link to="/portfolio">Explore the portfolio →</Link>
        </div>
        <div className="home-diary-grid">
          {visualDiary.map(([image, title, location], index) => (
            <Link className="home-diary-tile" to="/portfolio" key={image} aria-label={`${title}, ${location}`}>
              <img src={image} alt={`${title} — ${location}`} loading="lazy" />
              <span><small>{String(index + 1).padStart(2, "0")} · {location}</small><strong>{title}</strong></span>
            </Link>
          ))}
        </div>
      </section>

      <section className="home-proof" aria-label="Why buy from MayaChitro">
        <div><span>01</span><strong>Human-made originals</strong><p>Photographed and curated by MayaChitro—not generated imagery.</p></div>
        <div><span>02</span><strong>Know what you receive</strong><p>File, format, resolution and license details appear before purchase.</p></div>
        <div><span>03</span><strong>Secure digital delivery</strong><p>Stripe checkout with purchased files available from your account.</p></div>
        <div><span>04</span><strong>Support from the artist</strong><p>Questions about usage or licensing can be reviewed before you buy.</p></div>
      </section>

      {roomPreviewProduct && (
        <AiRoomPreview
          artworkUrl={getCardImage(roomPreviewProduct)}
          productTitle={roomPreviewProduct.title || "MayaChitro artwork"}
          onClose={() => setRoomPreviewProduct(null)}
        />
      )}

    </main>
  );
};

export default Home;
